import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, X, ChevronUp, ChevronDown, Printer, Pencil, CheckCircle2, Circle, Trash2 } from 'lucide-react';
import api from '../utils/api';
import { format, parseISO } from 'date-fns';
import OrderDetailModal from '../components/orders/OrderDetailModal';
import OrderModal from '../components/orders/OrderModal';
import DocumentPreview from '../components/documents/DocumentPreview';

const STATUS_COLORS = { new: '#F59E0B', sent: '#3B82F6', completed: '#10B981' };
const STATUS_ROW_BG  = { new: 'bg-amber-50/60 dark:bg-amber-900/10', sent: 'bg-blue-50/60 dark:bg-blue-900/10', completed: 'bg-emerald-50/60 dark:bg-emerald-900/10' };

export default function Orders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showOpenOnly, setShowOpenOnly] = useState(() => {
    try { return JSON.parse(localStorage.getItem('orders_showOpenOnly')) ?? true; } catch { return true; }
  });
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [pages, setPages] = useState(1);

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [printPreview, setPrintPreview] = useState(null);
  const [printLoadingId, setPrintLoadingId] = useState(null);

  async function handleDirectPrint(o) {
    setPrintLoadingId(o.id);
    try {
      const templatesRes = await api.get('/documents/templates');
      const tpl = templatesRes.data.find((t) => t.is_default_order) || templatesRes.data[0];
      if (!tpl) { alert('לא נמצאה תבנית הזמנה. צור תבנית בעמוד המסמכים.'); return; }
      const renderRes = await api.post('/documents/render', {
        template_id: tpl.id,
        customer_id: o.customer_id,
        order_id: o.id,
      });
      setPrintPreview({
        rendered_html: renderRes.data.html,
        template_name: `הזמנה #${o.order_number}`,
        customer_name: o.customer_name,
      });
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה ביצירת מסמך');
    } finally {
      setPrintLoadingId(null);
    }
  }

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_by: sortBy, sort_dir: sortDir };
      if (search) params.search = search;
      if (status) params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await api.get('/orders', { params });
      setOrders(res.data.data);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, status, dateFrom, dateTo, sortBy, sortDir]);

  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => { setPage(1); }, [search, status, dateFrom, dateTo, showOpenOnly]);
  useEffect(() => { localStorage.setItem('orders_showOpenOnly', JSON.stringify(showOpenOnly)); }, [showOpenOnly]);

  async function changeStatus(orderId, newStatus) {
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      loadOrders();
    } catch {
      alert('שגיאה בעדכון סטטוס');
    }
  }

  async function deleteOrder(o) {
    if (!window.confirm(`למחוק הזמנה #${o.order_number}?`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      loadOrders();
    } catch {
      alert('שגיאה במחיקת הזמנה');
    }
  }

  async function togglePaid(orderId, currentVal) {
    try {
      await api.put(`/orders/${orderId}/paid`, { is_paid: !currentVal });
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, is_paid: !currentVal } : o));
    } catch {
      alert('שגיאה בעדכון תשלום');
    }
  }

  function handleSort(col) {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  }

  // "פתוחות בלבד" — keep non-completed OR completed-but-unpaid
  const displayedOrders = showOpenOnly
    ? orders.filter((o) => o.status !== 'completed' || !o.is_paid)
    : orders;

  function rowBg(o) {
    if (o.status === 'completed' && !o.is_paid) return 'bg-red-100 dark:bg-red-900/30';
    return STATUS_ROW_BG[o.status] || '';
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <ShoppingBag className="text-primary-600 dark:text-primary-400" size={22} />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">הזמנות</h1>
        <span className="text-sm text-gray-500 dark:text-gray-400">({total.toLocaleString()})</span>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש לפי שם לקוח..."
              className="w-full pr-9 pl-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">כל הסטטוסים</option>
            <option value="new">הזמנה חדשה</option>
            <option value="sent">נשלחה</option>
            <option value="completed">הסתיימה</option>
          </select>

          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          <span className="text-gray-400 text-sm">עד</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOpenOnly}
              onChange={(e) => setShowOpenOnly(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            פתוחות בלבד
          </label>

          {(search || status || dateFrom || dateTo) && (
            <button onClick={() => { setSearch(''); setStatus(''); setDateFrom(''); setDateTo(''); }}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <X size={14} /> נקה
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <SortTh col="order_number" label="#" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                <SortTh col="customer_name" label="לקוח" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">עיר</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">סטטוס</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">שולם</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400">סכום</th>
                <SortTh col="created_at" label="תאריך" sortBy={sortBy} sortDir={sortDir} onClick={handleSort} />
                <th className="px-3 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {loading && displayedOrders.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center"><div className="flex justify-center"><div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" /></div></td></tr>
              ) : displayedOrders.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-400">אין הזמנות</td></tr>
              ) : (
                displayedOrders.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => setSelectedOrderId(o.id)}
                    className={`border-b border-gray-100 dark:border-gray-700/50 hover:brightness-95 dark:hover:brightness-110 cursor-pointer ${rowBg(o)}`}
                  >
                    <td className="px-4 py-3 text-center text-gray-500 dark:text-gray-400 font-mono">#{o.order_number}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/customers/${o.customer_id}`); }}
                        className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {o.customer_name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">{o.customer_city || '—'}</td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={o.status}
                        onChange={(e) => changeStatus(o.id, e.target.value)}
                        className="text-xs px-2 py-1.5 rounded-lg border focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700"
                        style={{ borderColor: STATUS_COLORS[o.status], color: STATUS_COLORS[o.status] }}
                      >
                        <option value="new">הזמנה חדשה</option>
                        <option value="sent">נשלחה</option>
                        <option value="completed">הסתיימה</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => togglePaid(o.id, o.is_paid)}
                        title={o.is_paid ? 'סמן כלא שולם' : 'סמן כשולם'}
                        className={`p-1 rounded-full transition-colors ${o.is_paid ? 'text-emerald-500 hover:text-emerald-700' : 'text-gray-300 dark:text-gray-600 hover:text-emerald-500'}`}
                      >
                        {o.is_paid ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">
                      {o.total_amount ? `₪${Number(o.total_amount).toLocaleString()}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-400 dark:text-gray-500 text-xs">
                      {format(parseISO(o.created_at), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-0.5 justify-center">
                        <button
                          onClick={() => setEditingOrder(o)}
                          title="עריכה"
                          className="p-1.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => handleDirectPrint(o)}
                          disabled={printLoadingId === o.id}
                          title="הדפסה"
                          className="p-1.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50"
                        >
                          <Printer size={13} />
                        </button>
                        <button
                          onClick={() => deleteOrder(o)}
                          title="מחיקה"
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-500">עמוד {page} מתוך {pages}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700">הקודם</button>
              <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages}
                className="px-3 py-1.5 text-sm rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700">הבא</button>
            </div>
          </div>
        )}
      </div>

      {printPreview && (
        <DocumentPreview doc={printPreview} onClose={() => setPrintPreview(null)} />
      )}

      {selectedOrderId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
          onStatusChanged={loadOrders}
        />
      )}

      {editingOrder && (
        <OrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={() => { setEditingOrder(null); loadOrders(); }}
        />
      )}
    </div>
  );
}

function SortTh({ col, label, sortBy, sortDir, onClick }) {
  const active = sortBy === col;
  return (
    <th className="px-4 py-3 text-center font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => onClick(col)}>
      <span className="flex items-center gap-1 justify-center">
        {label}
        {active ? (sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />) : <ChevronDown size={14} className="opacity-30" />}
      </span>
    </th>
  );
}
